import React, { useMemo } from 'react';
import PropTypes from "prop-types";
import { reduxForm, Field, getFormValues, clearAsyncError } from 'redux-form';
import { Modal, Button, ProgressIndicator, ReduxControls } from 'rhombus-react';

import { getFilenameExtension, noop } from 'utils/common';
import { asyncValidateSingleRename, uniqueName, baseNameRequired } from "./validators";
import { assetName, richloadName } from "widgets/forms/validators/Assets";

import { useDispatch, useSelector } from 'react-redux';

const RENAME_SINGLE_ASSET = "rename-single-asset";

function RenameSingleModal(props) {
	const dispatch = useDispatch();
	const {
		assetType,
		name,

		onBack,
		onSingleRename,
		handleSubmit,

		asyncValidating,
	} = props;

	let { newName }	= useSelector(getFormValues(RENAME_SINGLE_ASSET));
	let extension = `.${getFilenameExtension(name)}`;
	const nameValidator = useMemo(() => {
		return (assetType === "richloads") ? richloadName : assetName;
	}, [assetType]);

	function submitName(values){
		return onSingleRename(values.newName);
	}

	function renderContent(){
		return (
			<div className="c-modal-body">
				<ProgressIndicator isLoading={asyncValidating} />
				<div className="fields">
					<form id={RENAME_SINGLE_ASSET} onSubmit={handleSubmit(submitName)}>
						<Field
							name = "newName"
							component = {ReduxControls.Input}
							label = "New Name for Uploaded Asset"
							post = {extension}
							normalize = {value => `${value}${extension}`}
							format = {value => value.substring(0, value.lastIndexOf("."))}
							required
							validate = {[baseNameRequired, uniqueName, nameValidator]}
						/>
					</form>
				</div>
			</div>
		);
	}

	function renderFooter(){
		return (
			<>
				<Button onClick={() => onBack()} theme="default">Back</Button>
				<Button
					type = "submit"
					form = {RENAME_SINGLE_ASSET}
					theme = "blue"
					disabled = {!newName || asyncValidating}
					onClick={() => {
						/*
							Normally, you can't resubmit while there's an asyncError present,
							and you haven't changed the errored value, but we want to allow users to
							try again with the same values, in case there was an error checking
							for name collisions for their new name, because this flow ends by
							overwriting files.
						*/
						dispatch(clearAsyncError(RENAME_SINGLE_ASSET, "newName"));
					}}
				>
					Rename Asset
				</Button>
			</>
		);
	}

	let modalProps = {
		type: "modal",
		title: `Rename Uploaded Asset - ${name}`,
		allowFullScreen: false,
		className: "upload-error-modal rename-single-modal",
		xClose: false,
		bladeProps: {
			verticallyCenter: true,
		},
		renderContent,
		renderFooter,
	};

	return <Modal {...modalProps} />;
}

RenameSingleModal.displayName = "RenameSingleModal";

RenameSingleModal.propTypes = {
	libraryId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	assetType: PropTypes.oneOf([
		"images",
		"videos",
		"richloads",
		"after-effects",
		// "audios",
	]), // Used to check for name collision.  Should be pluralized, e.g. "videos"
	name: PropTypes.string,
	previousNames: PropTypes.arrayOf(PropTypes.string),
	conflicts: PropTypes.arrayOf(PropTypes.shape({
		name: PropTypes.string,
	})),
	onBack: PropTypes.func,
	onSingleRename: PropTypes.func,
};

RenameSingleModal.defaultProps = {
	previousNames: [],
	conflicts: [],
	onBack: noop,
	onSingleRename: noop,
};


export default reduxForm({
	form: RENAME_SINGLE_ASSET,
	asyncValidate: asyncValidateSingleRename,
	asyncBlurFields: [],
	initialValues: {
		newName: "",
	},
})(RenameSingleModal);
